/**
 * Lab-upload step (board card 7237).
 *
 * A controlled product must not walk from the driver's-license step straight
 * into checkout. `resolveNextRoute` sends it here instead, and this page is the
 * blocking step: the customer cannot continue until a lab document is attached
 * and accepted by the server.
 *
 * ⚠️ The BLOCK this page performs is a UX affordance, not the guard. Two things
 * behind it are the actual gate, because a disabled button is defeated by typing
 * a URL:
 *   1. `LabGate` in `web/App.jsx` refuses `/checkout` for a controlled product
 *      whose submission carries no lab document.
 *   2. `api/routes/intakeRoutes.js` enforces the format/size allowlist
 *      (`DOCUMENT_MIME_TYPES`) — the client checks below only spare the customer
 *      a round trip.
 *
 * The uploader interaction is deliberately the SAME shape as the driver's-licence
 * and card-1254 lab controls in `IntakePage.jsx`: a hidden `<input type="file">`
 * behind a `<label htmlFor>` drop target, the attached filename with a "Click to
 * change" affordance, an inline `role="alert"` error, and an accept hint that
 * names the permitted formats.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import * as productService from '../../services/productService';
import * as intakeService from '../../services/intakeService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { isControlledSubstanceProduct, resolvePostLabRoute } from '../../utils/intakeRouting';
import styles from './LabUploadPage.module.css';

/**
 * Mirrors `DOCUMENT_MIME_TYPES` in `api/middleware/upload.js`. The hint text is
 * DERIVED from this list rather than written out beside it, so the two cannot
 * drift the way the intake route's comment did before card 7236 removed it.
 */
const ACCEPTED_MIME_TYPES = Object.freeze([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/tiff'
]);

const ACCEPTED_LABEL = 'PDF, JPG, PNG, HEIC, WEBP or TIFF';
const MAX_MB = 10;
const MAX_BYTES = MAX_MB * 1024 * 1024;

const formatMb = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

/**
 * Pure: why this file is unacceptable, or null when it is fine.
 * `.heic` files frequently arrive with an EMPTY `type` from iOS Safari, so an
 * extension fallback is checked before refusing — otherwise the very format the
 * server was widened to accept would be blocked on the client.
 */
const validateLabFile = (file) => {
  if (!file) return 'Please choose a file.';
  const name = (file.name ?? '').toLowerCase();
  const byExtension = /\.(pdf|jpe?g|png|heic|heif|webp|tiff?)$/.test(name);
  if (!ACCEPTED_MIME_TYPES.includes(file.type) && !byExtension) {
    return `That file type is not accepted. Please upload ${ACCEPTED_LABEL}.`;
  }
  if (file.size > MAX_BYTES) {
    return `That file is ${formatMb(file.size)} MB. Please upload a file under ${MAX_MB} MB.`;
  }
  return null;
};

const LabUploadPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('product');

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [labFile, setLabFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /**
   * Rebuild the query string this step was entered with, so the deep-link
   * contract `IntakePage` already forwards (`product` / `supplyMonths` /
   * `dosage`) survives the extra hop instead of being dropped here.
   */
  const onwardQuery = useCallback((productSlug) => {
    const supplyMonths = searchParams.get('supplyMonths') ?? sessionStorage.getItem('selectedSupplyMonths') ?? '';
    const dosage = searchParams.get('dosage') ?? sessionStorage.getItem('selectedDosageId') ?? '';
    return [
      `product=${encodeURIComponent(productSlug)}`,
      supplyMonths ? `supplyMonths=${encodeURIComponent(supplyMonths)}` : '',
      dosage ? `dosage=${encodeURIComponent(dosage)}` : ''
    ].filter(Boolean).join('&');
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      if (!slug) {
        navigate('/catalog', { replace: true });
        return;
      }
      try {
        const response = await productService.getProductBySlug(slug);
        if (cancelled) return;
        const loaded = response?.product ?? null;
        setProduct(loaded);

        // Defensive: this step exists only for controlled products. Reaching it
        // for anything else means a stale link, so hand straight back to the
        // route the product would have taken — never strand the customer here.
        if (loaded && !isControlledSubstanceProduct(loaded)) {
          navigate(`${resolvePostLabRoute(loaded)}?${onwardQuery(loaded.slug)}`, { replace: true });
          return;
        }
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Could not load this product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProduct();
    return () => { cancelled = true; };
  }, [slug, navigate, onwardQuery]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    // A cancelled picker fires change with no file; keep whatever was attached.
    if (!file) return;
    const problem = validateLabFile(file);
    if (problem) {
      setUploadError(problem);
      setLabFile(null);
      e.target.value = '';
      return;
    }
    setUploadError('');
    setLabFile(file);
  };

  const handleContinue = async () => {
    const problem = validateLabFile(labFile);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setSubmitting(true);
    try {
      await intakeService.submitLabResults(product.id, labFile);
      toast.success('Lab work uploaded');
      navigate(`${resolvePostLabRoute(product)}?${onwardQuery(product.slug)}`);
    } catch (error) {
      // Surface the SERVER's refusal verbatim — it is the authority on the
      // format set, and paraphrasing it here is how the two drift apart.
      setUploadError(error.message || 'Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;

  if (!product) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Product not found</h1>
          <p className={styles.subtitle}>We could not load this treatment. Please choose it again from the catalog.</p>
          <button type="button" className={styles.continueBtn} onClick={() => navigate('/catalog')}>
            Back to catalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Lab work required</h1>
        <p className={styles.subtitle}>
          {product.name} is a controlled medication, and this medication requires labs.
          Please upload your lab work to continue.
        </p>

        <div className={styles.labUpload}>
          <input
            type="file"
            accept={ACCEPTED_MIME_TYPES.join(',')}
            onChange={handleFileChange}
            className={styles.fileInput}
            id="lab-upload-file"
          />
          <label htmlFor="lab-upload-file" className={styles.uploadLabel}>
            {labFile ? (
              <div className={styles.uploadedFile}>
                <span className={styles.fileIcon}>✓</span>
                <span className={styles.fileName}>{labFile.name}</span>
                <span className={styles.changeFile}>Click to change</span>
              </div>
            ) : (
              <>
                <span className={styles.uploadIcon}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </span>
                <span className={styles.uploadText}>Upload your lab work</span>
                <span className={styles.uploadHint}>{ACCEPTED_LABEL} · up to {MAX_MB} MB</span>
              </>
            )}
          </label>
          {uploadError && (
            <p className={styles.uploadError} role="alert">{uploadError}</p>
          )}
        </div>

        <button
          type="button"
          className={styles.continueBtn}
          onClick={handleContinue}
          disabled={!labFile || submitting}
        >
          {submitting ? 'Uploading…' : 'Continue'}
        </button>
        <p className={styles.note}>
          You cannot continue to checkout until your lab work is attached.
        </p>
      </div>
    </div>
  );
};

export default LabUploadPage;
